import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FILE_SCOPE_REQUEST } from '../../../constants/file'
import { useUk } from '../../../hooks'
import { CentrifugeProvider } from '../../Centrifuge'
import { Chatroom } from '../../Chatroom'
import { IconSpinner } from '../../Icon'
import {
  REQUEST_PROFILE_TAB_CHATS,
  REQUEST_PROFILE_TAB_DOCUMENTS,
} from '../RequestProfile.constants'
import { useRequestProfileContext } from '../RequestProfile.hooks'

import { REQUEST_PROFILE_TAB_CHATS_TITLE_KEY } from './RequestProfileTabChats.constants'
import {
  useRequestProfileTabChatsActiveSubTab,
  useRequestProfileTabChatsCounters,
} from './RequestProfileTabChats.hooks'
import {
  getIsRequestProfileTabChatsPrivate,
  getIsRequestProfileTabChatsPublic,
  getRequestProfileTabChatsAllowedSubTabs,
  getRequestProfileTabChatsChatroomUuid,
  getRequestProfileTabChatsOrder,
} from './RequestProfileTabChats.utils'
import { RequestProfileTabChatsFullScreenModal } from './RequestProfileTabChatsFullScreenModal'
import { RequestProfileTabChatsSubTabs } from './RequestProfileTabChatsSubTabs'

import * as styles from './RequestProfileTabChats.module.css'

function RequestProfileTabChats({ onLoadCounters }) {
  const { t } = useTranslation()
  const { handleEntityReload, outboundUuid, requestData } =
    useRequestProfileContext()

  const { config: ukConfig } = useUk()

  const subTabsOrder = getRequestProfileTabChatsOrder(
    ukConfig.reversed_request_chat_tabs
  )

  const allowedSubTabs = getRequestProfileTabChatsAllowedSubTabs({
    requestData,
    subTabsOrder,
  })

  const {
    counters,
    handleLoadCounters: handleLoadChatsCounters,
    isCountersReady,
  } = useRequestProfileTabChatsCounters(requestData?.id)

  const { activeSubTab, handleSetActiveSubTab, isResolvingInitialSubTab } =
    useRequestProfileTabChatsActiveSubTab({
      allowedSubTabs,
      counters,
      isCountersReady,
    })

  const [isFullScreen, setFullScreen] = useState(false)

  const messageDraftRef = useRef(null)

  function handleOpenFullScreen() {
    setFullScreen(true)
  }

  function handleCloseFullScreen() {
    setFullScreen(false)
  }

  // Refresh the chats subtab counters, the parent profile tab counters, and the
  // list row chat tags so badges everywhere reflect the new sent/read state.
  function handleReloadCounters() {
    handleLoadChatsCounters()
    onLoadCounters?.([REQUEST_PROFILE_TAB_CHATS, REQUEST_PROFILE_TAB_DOCUMENTS])
  }

  function handleChatroomCreated() {
    handleEntityReload?.()
    handleReloadCounters()
  }

  // `allowedSubTabs` is empty while permissions are still loading (or if the
  // user has no chat access); `activeSubTab` is then null, so there is no
  // sub-tab to render yet.
  if (allowedSubTabs.length === 0) {
    return null
  }

  // Waiting on the chats counters to decide which sub-tab to open on entry.
  if (isResolvingInitialSubTab) {
    return (
      <div className={styles.requestProfileTabChats}>
        <div className={styles.loader}>
          <IconSpinner />
        </div>
      </div>
    )
  }

  const chatroomUuid = getRequestProfileTabChatsChatroomUuid({
    activeSubTab,
    requestData,
  })

  const isPublic = getIsRequestProfileTabChatsPublic(activeSubTab)

  const isPrivate = getIsRequestProfileTabChatsPrivate(activeSubTab)

  const title = t(REQUEST_PROFILE_TAB_CHATS_TITLE_KEY[activeSubTab])

  const chatroomProps = {
    buildingId: requestData?.building?.id,
    createChatroomParams: { request: requestData?.id },
    filePreviewProps: {
      scope: FILE_SCOPE_REQUEST,
      subject: requestData?.id,
    },
    isPrivate,
    isPublic,
    messageDraftRef,
    outboundUuid,
    requestId: requestData?.id,
    requestPermissions: requestData?.permissions,
    title,
    uuid: chatroomUuid,
    visibleToDwellers: requestData?.visibleToDwellers,
    onChatroomCreated: handleChatroomCreated,
    onMessageDeleted: handleReloadCounters,
    onMessageSent: handleReloadCounters,
    onReadBottomReached: handleReloadCounters,
  }

  const content = (
    <div className={styles.requestProfileTabChats}>
      <RequestProfileTabChatsSubTabs
        activeSubTab={activeSubTab}
        allowedSubTabs={allowedSubTabs}
        counters={counters}
        onSetActiveSubTab={handleSetActiveSubTab}
      />
      {isFullScreen ? (
        <RequestProfileTabChatsFullScreenModal
          {...chatroomProps}
          key={chatroomUuid || activeSubTab}
          onClose={handleCloseFullScreen}
        />
      ) : (
        <div className={styles.chatWrap}>
          <Chatroom
            {...chatroomProps}
            key={chatroomUuid || activeSubTab}
            onToggleFullScreen={handleOpenFullScreen}
          />
        </div>
      )}
    </div>
  )

  return outboundUuid ? (
    <CentrifugeProvider outboundUuid={outboundUuid}>
      {content}
    </CentrifugeProvider>
  ) : (
    content
  )
}

export default RequestProfileTabChats
